import {
  ApiTokenListResponse,
  CalendarEventOccurrence,
  EventOccurrenceV2DTO,
  EventRsvpCustomerDTO,
  TokenPageableRequestParams
} from "@gymflow/types";
import {
  infiniteQueryOptions,
  mutationOptions,
  QueryKey,
  queryOptions,
} from "@tanstack/react-query";

import { api } from "../../api";
import { apiV2 } from "../../apiV2";
import { queryClient } from "../../QueryClient";
import { tokenPagination } from "../../tokenPagination";
import { memberApiAccess } from "./memberApiAccess";

const keys = {
  all: () => ["memberApiEventOccurrence"] as QueryKey,
  id: (id: unknown) => [...keys.all(), "id", id] as QueryKey,
  memberId: (id: unknown) => [...keys.all(), "memberId", id] as QueryKey,
  list: (filter: unknown) => [...keys.all(), "list", filter] as QueryKey,
  upcoming: (memberId: unknown, params: unknown) =>
    [...keys.memberId(memberId), "upcoming", params] as QueryKey,
  past: (memberId: unknown, params: unknown) =>
    [...keys.memberId(memberId), "past", params] as QueryKey,
};

export const memberApiEventOccurrence = {
  keys,
  queries: {
    id: (id: number | undefined) =>
      queryOptions({
        queryKey: keys.id(id),
        queryFn: async () => {
          return (
            await api.get<EventOccurrenceV2DTO>(
              `/customer/event/occurrence/v2/${id}`,
            )
          ).data;
        },
        enabled: id !== undefined,
      }),
  },
  infiniteQueries: {
    list: (
      params: {
        dateFrom?: string;
        dateTo?: string;
        includeBookedCounts?: boolean;
        includeWaitingCounts?: boolean;
        eventHostId?: string[];
        facilityId?: number[];
        activityId?: number[];
        isPublicEvent?: boolean;
        activityCategoryId?: number[];
      } & TokenPageableRequestParams,
    ) =>
      infiniteQueryOptions({
        queryKey: keys.list(params),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<ApiTokenListResponse<CalendarEventOccurrence>>(
              "customer/calendar/event-occurrences",
              {
                params: {
                  ...params,
                  nextPageToken: pageParam,
                },
              },
            )
          ).data;
        },
        ...tokenPagination,
      }),
    upcoming: (
      memberId: string | undefined,
      params: {
        activityId?: number[];
        eventHostId?: string[];
        activityCategoryId?: number[];
        eventFacilityId?: number[];
      } & TokenPageableRequestParams,
    ) =>
      infiniteQueryOptions({
        queryKey: keys.upcoming(memberId, params),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<ApiTokenListResponse<EventRsvpCustomerDTO>>(
              `/customer/event-rsvp/upcoming`,
              {
                params: {
                  ...params,
                  nextPageToken: pageParam,
                },
              },
            )
          ).data;
        },
        enabled: memberId !== undefined,
        ...tokenPagination,
      }),
    past: (
      memberId: string | undefined,
      params: {
        activityId?: number[];
        eventHostId?: string[];
        activityCategoryId?: number[];
        eventFacilityId?: number[];
      } & TokenPageableRequestParams,
    ) =>
      infiniteQueryOptions({
        queryKey: keys.past(memberId, params),
        queryFn: async ({ pageParam }) => {
          return (
            await api.get<ApiTokenListResponse<EventRsvpCustomerDTO>>(
              `/customer/event-rsvp/past`,
              {
                params: {
                  ...params,
                  nextPageToken: pageParam,
                },
              },
            )
          ).data;
        },
        enabled: memberId !== undefined,
        ...tokenPagination,
      }),
  },
  mutations: {
    createRsvp: () =>
      mutationOptions({
        mutationFn: async ({
          occurrenceId,
        }: {
          occurrenceId: number;
          userId: string;
        }) => {
          return (
            await api.put<unknown>(`customer/event-rsvp`, {
              occurrenceId,
            })
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: keys.all(),
            }),
            queryClient.invalidateQueries({
              queryKey: apiV2.member.member.keys.id(userId),
            }),
            queryClient.invalidateQueries({
              queryKey: memberApiAccess.keys.perUser(userId),
            }),
          ]);
        },
      }),
    bookRsvp: () =>
      mutationOptions({
        mutationFn: async ({ rsvpId }: { rsvpId: number; userId: string }) => {
          return (await api.put<unknown>(`customer/event-rsvp/${rsvpId}/book`))
            .data;
        },
        onSettled: async (_, __, { userId }) => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: keys.all(),
            }),
            queryClient.invalidateQueries({
              queryKey: apiV2.member.member.keys.id(userId),
            }),
            queryClient.invalidateQueries({
              queryKey: memberApiAccess.keys.perUser(userId),
            }),
          ]);
        },
      }),
    cancelRsvp: () =>
      mutationOptions({
        mutationFn: async ({ rsvpId }: { rsvpId: number; userId: string }) => {
          return (
            await api.put<unknown>(`customer/event-rsvp/${rsvpId}/cancel`)
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: keys.all(),
            }),
            queryClient.invalidateQueries({
              queryKey: apiV2.member.member.keys.id(userId),
            }),
            queryClient.invalidateQueries({
              queryKey: memberApiAccess.keys.perUser(userId),
            }),
          ]);
        },
      }),
    lateCancel: () =>
      mutationOptions({
        mutationFn: async ({
          id,
          paymentMethodId,
        }: {
          id: number;
          userId: string;
          paymentMethodId?: string;
        }) => {
          return (
            await api.put<{ invoiceNumber?: string }>(
              `customer/event-rsvp/${id}/late-cancel`,
              {
                paymentMethodId,
              },
            )
          ).data;
        },
        onSettled: async (_, __, { userId }) => {
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: keys.all(),
            }),
            queryClient.invalidateQueries({
              queryKey: apiV2.member.member.keys.id(userId),
            }),
            queryClient.invalidateQueries({
              queryKey: memberApiAccess.keys.perUser(userId),
            }),
          ]);
        },
      }),
  },
};
